/**
 * Sidebar Navigation Configuration
 *
 * Config-driven sidebar — edit this file to add, remove, or reorganise nav items.
 * Items are filtered at runtime by feature flags, user roles, and Builder Mode.
 *
 * Mode hierarchy (see docs/PAGE_GRAMMAR.md):
 *   - "Work" — daily users get work done (Home, Chat, Projects, Spaces, Inbox)
 *   - "Setup" — adding capability (Skills, Connections, Routines)
 *   - "Builder" — developer surfaces (Components, Style Guide, Activity, Voice/Video examples)
 *     Hidden by default; toggled via the user-menu Builder Mode switch.
 *
 * When forking this starter:
 * 1. Edit the sections and items below to match your product
 * 2. Set feature flags in .dev.vars to hide items you don't need
 * 3. The module code stays in the repo as reference implementations
 *
 * @see src/shared/config/features.ts for feature flag definitions
 * @see src/client/lib/builder-mode.tsx for the Builder Mode toggle
 */
import type { LucideIcon } from 'lucide-react'

export interface NavItem {
  /** Route path */
  to: string
  /** Display label */
  label: string
  /** Lucide icon component */
  icon: LucideIcon
  /** Only show if this feature flag is true (from features config) */
  feature?: string
  /** Minimum role required. Omit = visible to all roles. */
  minRole?: 'user' | 'manager' | 'admin'
  /**
   * Only show when Builder Mode is enabled (developer surfaces). Hidden
   * by default for normal users. The toggle lives in the user menu.
   */
  builderOnly?: boolean
}

export interface NavSection {
  /** Section header label */
  label: string
  /** Nav items in this section */
  items: NavItem[]
  /** If true, section starts collapsed in the sidebar */
  defaultCollapsed?: boolean
  /**
   * If true, the entire section is hidden unless Builder Mode is on.
   * Use this for the "Builder" group; per-item `builderOnly` is for
   * mixing builder items into other sections.
   */
  builderOnly?: boolean
}

/**
 * Sidebar navigation sections.
 *
 * Three tiers, each answering a different user-intent question:
 *
 *   - Work (always visible) — daily actions. "What kind of work am I
 *     doing right now?" One-off chats, ongoing projects, team spaces,
 *     scheduled routines, queued items needing attention.
 *
 *   - Setup (collapsed) — configuration. "How does the AI behave?"
 *     Connections, skills, agents, chat-driven config. Most users
 *     touch this on day 2-3 (plug Gmail in), then rarely.
 *
 *   - Insights (collapsed) — observability. "What has the AI done?"
 *     Approvals queue, agent runs/cost charts, audit log, files
 *     produced, structured extraction.
 *
 *   - Builder (collapsed, builder-mode gated) — fork-author surfaces.
 *     Component showcase, style guide, voice/video worked examples.
 *
 * Restructure 2026-05-02: Routines moved Work-side (it's a daily
 * intent, not a setup step). Connections / Skills / Agents / Admin chat
 * collapsed into Setup (configuration concerns). Insights collapsed by
 * default (status reading, not daily action).
 *
 * Settings / Admin Panel live in the user-menu dropdown to keep the
 * sidebar focused on primary destinations.
 */
// siu-dou: no sidebar nav — journal pages use their own layout.
// /dashboard/* routes are accessible for admin/settings but not surfaced.
export const NAV_SECTIONS: NavSection[] = []
